import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Upload, Download, CheckCircle, XCircle, File, AlertTriangle } from 'lucide-react';

const ImportSection = ({ type, label }) => {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f?.name.endsWith('.csv')) setFile(f);
    else toast.error('Please upload a CSV file');
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handleImport = async () => {
    if (!file) { toast.error('Please select a file'); return; }
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/import/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      if (res.data.accepted > 0) toast.success(`${res.data.accepted} records imported`);
      if (res.data.rejected > 0) toast.warning(`${res.data.rejected} rows had errors`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    window.open(`${API}/export/template/${type}`, '_blank');
  };

  const total = result ? result.accepted + result.rejected : 0;
  const acceptedPct = total > 0 ? Math.round(result.accepted / total * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Template download */}
      <Card className="bg-white shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-800 text-sm">Step 1: Download Template</p>
              <p className="text-xs text-slate-500 mt-0.5">Use the template to ensure correct column format</p>
            </div>
            <Button onClick={downloadTemplate} variant="outline" className="gap-2 bg-white"
              data-testid="import-template-download-button">
              <Download size={14} /> Download Template
            </Button>
          </div>
        </CardContent>
      </Card>
      {/* Dropzone */}
      <Card className="bg-white shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Step 2: Upload CSV</CardTitle></CardHeader>
        <CardContent>
          <div
            data-testid="import-dropzone"
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
              dragging ? 'border-cyan-400 bg-cyan-50' : 'border-slate-200 hover:border-cyan-300 hover:bg-slate-50'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
          >
            <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <File size={36} className="text-cyan-500" />
                <p className="font-medium text-slate-700">{file.name}</p>
                <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                <button className="text-xs text-red-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                  Remove file
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload size={36} className="text-slate-300" />
                <p className="text-slate-500">Drag & drop your CSV here</p>
                <p className="text-xs text-slate-400">or click to browse</p>
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleImport}
              disabled={!file || loading}
              className="gap-2 font-semibold"
              className="btn-primary" style={{ background: '#1D4ED8', color: 'white' }}
              data-testid="import-submit-button"
            >
              <Upload size={14} />
              {loading ? 'Importing...' : `Import ${label}`}
            </Button>
          </div>
        </CardContent>
      </Card>
      {/* Results */}
      {result && (
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Import Results</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle size={20} className="text-green-600" />
                <div>
                  <p className="font-semibold text-green-800 text-lg">{result.accepted}</p>
                  <p className="text-xs text-green-600">Records Imported</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                <XCircle size={20} className="text-red-500" />
                <div>
                  <p className="font-semibold text-red-700 text-lg">{result.rejected}</p>
                  <p className="text-xs text-red-500">Rows Rejected</p>
                </div>
              </div>
            </div>
            {total > 0 && (
              <div>
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Success rate</span>
                  <span>{acceptedPct}%</span>
                </div>
                <Progress value={acceptedPct} className="h-2" />
              </div>
            )}
            {result.errors?.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">Row Errors:</p>
                  <button
                    onClick={() => {
                      // Download rejected rows as CSV
                      const rows = result.all_errors || result.errors || [];
                      const lines = ['row,error_message,data'];
                      rows.forEach(e => {
                        const errs = Array.isArray(e.errors) ? e.errors.join('; ') : (e.errors || '');
                        const data = e.data ? JSON.stringify(e.data).replace(/"/g, "'") : '';
                        lines.push(`${e.row},"${errs}","${data}"`);
                      });
                      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = 'import_errors.csv';
                      a.click();
                    }}
                    className="text-xs text-cyan-500 hover:text-cyan-700 flex items-center gap-1"
                  >
                    ↓ Download error CSV
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {result.errors.map((err, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs p-2 bg-red-50 rounded border border-red-100">
                      <AlertTriangle size={12} className="text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-red-700">Row {err.row}: {Array.isArray(err.errors) ? err.errors.join('; ') : err.errors}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default function ImportPage() {
  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Import Data</h1>
        <p className="text-slate-500 text-sm mt-1">Import historical EOD or sale records via CSV</p>
      </div>
      <Tabs defaultValue="eod">
        <TabsList className="bg-white/10 mb-6">
          <TabsTrigger value="eod" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-300">
            EOD Records
          </TabsTrigger>
          <TabsTrigger value="sales" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-300">
            Sales Records
          </TabsTrigger>
        </TabsList>
        <TabsContent value="eod">
          <ImportSection type="eod" label="EOD Records" />
        </TabsContent>
        <TabsContent value="sales">
          <ImportSection type="sales" label="Sales Records" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
